import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  useEffect,
  useState,
} from 'react';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronsRight,
  Download,
  FileText,
  Lock,
  Search,
  Sparkles,
  SquarePen,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { useParams } from 'react-router-dom';

import {
  InstructorRadarChart,
  type InstructorRadarChartDataset,
} from '../components/ui/Charts/InstructorRadarChart';
import { InstructorLoadingSkeleton } from '../components/layout/InstructorLoadingSkeleton';
import { InstructorToast } from '../components/layout/InstructorToast';
import { useInstructorToast } from '../components/layout/useInstructorToast';
import { getSubmissionFileDisplayNames } from '../lib/submissionFiles';
import {
  instructorService,
  type CourseSubmission,
  type Dimension,
  type InstructorCourse,
  type SubmissionDetails,
} from '../services/instructorService';

type SubmissionStatusFilter = 'all' | 'handed' | 'draft';
type SubmissionViewMode = 'submission' | 'student';

interface InstructorSubmissionRow extends CourseSubmission {
  course_id: string;
  course_code: string;
  course_title: string;
}

interface SubmissionListRow {
  key: string;
  submission: InstructorSubmissionRow;
  submissions: InstructorSubmissionRow[];
  displayName: string;
  secondary?: string;
}

interface SubmissionReviewBodyStyle extends CSSProperties {
  '--submission-drawer-width'?: string;
}

interface MiniRadarProps {
  dimensions: Dimension[];
  scores: SubmissionDetails['scores'];
}

interface RubricModalProps {
  dimensions: Dimension[];
  onClose: () => void;
  scores: SubmissionDetails['scores'];
  submission: InstructorSubmissionRow;
  submissionDetails: SubmissionDetails | null;
}

const statusFilters: { key: SubmissionStatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'handed', label: 'Handed In' },
  { key: 'draft', label: 'Draft only' },
];

const statusClassByStatus: Record<CourseSubmission['status'], string> = {
  submitted: 'instructor-status-submitted',
  scoring: 'instructor-status-scoring',
  scored: 'instructor-status-scored',
  reviewed: 'instructor-status-reviewed',
};

const getStatusClass = (status: CourseSubmission['status']) =>
  statusClassByStatus[status];

const getStatusLabel = (status: CourseSubmission['status']) => {
  const labels: Record<CourseSubmission['status'], string> = {
    submitted: 'Handed In',
    scoring: 'Scoring',
    scored: 'Scored',
    reviewed: 'Reviewed',
  };

  return labels[status];
};

const padDatePart = (value: number) => value.toString().padStart(2, '0');

const formatDate = (dateString: string) => {
  const date = new Date(dateString);

  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);

  return `${formatDate(dateString)} ${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
};

const sortSubmissionsNewestFirst = <T extends { submitted_at: string }>(items: T[]) =>
  [...items].sort(
    (first, second) =>
      new Date(second.submitted_at).getTime() -
      new Date(first.submitted_at).getTime()
  );

const getStudentGroupKey = (submission: InstructorSubmissionRow) =>
  submission.student_id || submission.student_email || submission.student_name;

const getSubmissionCountLabel = (count: number) =>
  `${count} submission${count === 1 ? '' : 's'}`;

const doesSubmissionMatchSearch = (
  submission: InstructorSubmissionRow,
  normalizedSearch: string
) =>
  submission.student_name.toLowerCase().includes(normalizedSearch) ||
  submission.student_email.toLowerCase().includes(normalizedSearch) ||
  submission.project_title.toLowerCase().includes(normalizedSearch) ||
  submission.name.toLowerCase().includes(normalizedSearch) ||
  submission.course_code.toLowerCase().includes(normalizedSearch);

const getAverageScore = (submission: CourseSubmission) => {
  const score = Number(submission.avg_score);

  return Number.isFinite(score) ? score : 0;
};

const getTotalScore = (submission: CourseSubmission) =>
  Math.round(getAverageScore(submission) * submission.scores_count);

const getScoreLabel = (submission: CourseSubmission) => {
  if (submission.scores_count === 0) {
    return 'Not scored';
  }

  return `${getTotalScore(submission)}/${submission.scores_count * 3}`;
};

const getScorePercent = (submission: CourseSubmission) =>
  Math.min(100, Math.max(0, (getAverageScore(submission) / 3) * 100));

const getDimensionCategory = (dimensionId: number) => {
  if (dimensionId <= 2) {
    return 'Purposefulness';
  }

  if (dimensionId <= 5) {
    return 'Disciplinary';
  }

  if (dimensionId <= 7) {
    return 'Integration';
  }

  return 'Critical Awareness';
};

const getDimensionDisplayLabel = (dimension: Dimension) =>
  dimension.short_label || dimension.label;

const miniRadarLabelById: Record<number, string> = {
  1: 'Problem Framing',
  2: 'Stake.',
  3: 'Range',
  4: 'Reasoning',
  5: 'Credibility',
  6: 'I.Number',
  7: 'I.Depth',
  8: 'Social Impact',
  9: 'Limitations',
};

const getMiniRadarLabel = (dimension: Dimension) =>
  miniRadarLabelById[dimension.id] || getDimensionDisplayLabel(dimension);

const getRubricLevel = (score?: number) => {
  if (score === undefined || !Number.isFinite(score)) {
    return null;
  }

  return Math.min(3, Math.max(0, Math.round(score)));
};

const matchesStatusFilter = (
  submission: CourseSubmission,
  filter: SubmissionStatusFilter
) => {
  if (filter === 'all') {
    return true;
  }

  if (filter === 'handed') {
    return submission.status !== 'scoring';
  }

  return submission.status === 'scoring';
};

const haveSameParticipants = (
  first: InstructorSubmissionRow,
  second: InstructorSubmissionRow
) => {
  if (first.project_type !== 'group' || second.project_type !== 'group') {
    return first.student_id === second.student_id;
  }

  const firstIds = [...first.participant_student_ids].sort();
  const secondIds = [...second.participant_student_ids].sort();

  return (
    firstIds.length === secondIds.length &&
    firstIds.every((studentId, index) => studentId === secondIds[index])
  );
};

const getWordCount = (text: string) =>
  text.trim().split(/\s+/).filter(Boolean).length;

const MiniRadar = ({ dimensions, scores }: MiniRadarProps) => {
  const visibleDimensions = dimensions.slice(0, 9);
  const scoreById = new Map(scores.map((score) => [score.dimension_id, score.score]));

  if (visibleDimensions.length === 0) {
    return <div className="instructor-mini-radar-empty">No score profile</div>;
  }

  const labels = visibleDimensions.map(getMiniRadarLabel);
  const datasets: InstructorRadarChartDataset[] = [
    {
      backgroundColor: 'rgba(0,61,124,0.15)',
      borderColor: '#003D7C',
      borderWidth: 2,
      data: visibleDimensions.map((dimension) => scoreById.get(dimension.id) ?? 0),
      label: 'Selected',
      pointBackgroundColor: '#003D7C',
      pointHoverRadius: 4,
      pointRadius: 2.5,
    },
  ];

  return (
    <InstructorRadarChart
      className="instructor-mini-radar"
      datasets={datasets}
      height={175}
      labels={labels}
      pointLabelFontSize={9}
      tickFontSize={8}
      tooltipDecimals={null}
    />
  );
};

const RubricModal = ({
  dimensions,
  onClose,
  scores,
  submission,
  submissionDetails,
}: RubricModalProps) => {
  const scoreById = new Map(scores.map((score) => [score.dimension_id, score]));
  const subtitle = submissionDetails
    ? `${submissionDetails.submission.student_name} · ${submissionDetails.submission.project_title} · ${submissionDetails.submission.name}`
    : `${submission.student_name} · ${submission.project_title} · ${submission.name}`;

  return (
    <div
      className="instructor-modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div aria-modal="true" className="instructor-rubric-modal" role="dialog">
        <button
          aria-label="Close rubric"
          className="instructor-modal-close"
          onClick={onClose}
          type="button"
        >
          <X strokeWidth={2.5} />
        </button>
        <h2>Rubric — Dimension Scores</h2>
        <p>{subtitle}</p>

        {dimensions.length === 0 ? (
          <div className="instructor-empty-state">No rubric dimensions available.</div>
        ) : (
          <table className="instructor-rubric-score-table">
            <thead>
              <tr>
                <th>Dimension</th>
                <th>Category</th>
                <th>Level</th>
                <th aria-label="Level bars" />
              </tr>
            </thead>
            <tbody>
              {dimensions.map((dimension) => {
                const score = scoreById.get(dimension.id);
                const level = getRubricLevel(score?.score);
                const color = dimension.color_hex || '#003d7c';

                return (
                  <tr key={dimension.id}>
                    <td>
                      <span className="instructor-rubric-score-dimension">
                        <span
                          className="instructor-rubric-score-dot"
                          style={{ backgroundColor: color }}
                        />
                        {getDimensionDisplayLabel(dimension)}
                      </span>
                    </td>
                    <td>{getDimensionCategory(dimension.id)}</td>
                    <td>
                      <span
                        className="instructor-rubric-score-level"
                        style={{ color }}
                      >
                        {level === null ? 'Not scored' : `Lv. ${level}`}
                      </span>
                    </td>
                    <td>
                      <span
                        className="instructor-rubric-score-bars"
                        style={{ color }}
                      >
                        {[1, 2, 3].map((segment) => (
                          <span
                            className={
                              level !== null && level >= segment
                                ? 'instructor-rubric-score-segment on'
                                : 'instructor-rubric-score-segment'
                            }
                            key={segment}
                          />
                        ))}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const InstructorSubmissions = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const [courses, setCourses] = useState<InstructorCourse[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [submissions, setSubmissions] = useState<InstructorSubmissionRow[]>([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [submissionDetails, setSubmissionDetails] = useState<SubmissionDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<SubmissionStatusFilter>('all');
  const [viewMode, setViewMode] = useState<SubmissionViewMode>('submission');
  const [courseFilter, setCourseFilter] = useState('all');
  const [suggestionText, setSuggestionText] = useState('');
  const [savingSuggestion, setSavingSuggestion] = useState(false);
  const [isRubricModalOpen, setIsRubricModalOpen] = useState(false);
  const [isSubmissionDrawerOpen, setIsSubmissionDrawerOpen] = useState(false);
  const [isCourseMenuOpen, setIsCourseMenuOpen] = useState(false);
  const [submissionDrawerWidth, setSubmissionDrawerWidth] = useState(420);
  const [downloadingFileKey, setDownloadingFileKey] = useState<string | null>(null);
  const [isResizingDrawer, setIsResizingDrawer] = useState(false);
  const { showToast, toast } = useInstructorToast();

  const courseFilteredSubmissions = submissions.filter((submission) => {
    const matchesCourse =
      courseFilter === 'all' || submission.course_id === courseFilter;

    return matchesCourse;
  });
  const courseAndStatusFilteredSubmissions = courseFilteredSubmissions.filter(
    (submission) => matchesStatusFilter(submission, filterStatus)
  );
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const submissionListRows: SubmissionListRow[] = courseAndStatusFilteredSubmissions.map(
    (submission) => ({
      key: submission.id,
      submission,
      submissions: [submission],
      displayName: submission.student_name,
      secondary:
        submission.project_type === 'group'
          ? `${submission.participant_names.length || 1} members`
          : undefined,
    })
  );
  const studentListRows: SubmissionListRow[] = Array.from(
    courseAndStatusFilteredSubmissions.reduce(
      (groups, submission) => {
        const key = getStudentGroupKey(submission);
        const group = groups.get(key) ?? [];

        groups.set(key, [...group, submission]);

        return groups;
      },
      new Map<string, InstructorSubmissionRow[]>()
    ).entries()
  )
    .flatMap(([studentKey, studentSubmissions]) => {
      const sortedStudentSubmissions = sortSubmissionsNewestFirst(studentSubmissions);
      const latestSubmission = sortedStudentSubmissions[0];

      if (!latestSubmission) {
        return [];
      }

      return [
        {
          key: `student-${studentKey}`,
          submission: latestSubmission,
          submissions: sortedStudentSubmissions,
          displayName: latestSubmission.student_name,
          secondary: getSubmissionCountLabel(sortedStudentSubmissions.length),
        },
      ];
    })
    .sort((first, second) =>
      first.displayName.localeCompare(second.displayName, undefined, {
        sensitivity: 'base',
      })
    );
  const listRows: SubmissionListRow[] = (
    viewMode === 'student' ? studentListRows : submissionListRows
  ).filter((row) => {
    return (
      normalizedSearch.length === 0 ||
      row.displayName.toLowerCase().includes(normalizedSearch) ||
      row.submissions.some((submission) =>
        doesSubmissionMatchSearch(submission, normalizedSearch)
      )
    );
  });
  const visibleSubmissions = Array.from(
    new Map(
      listRows
        .flatMap((row) => row.submissions)
        .map((submission) => [submission.id, submission])
    ).values()
  );
  const selectedSubmission =
    visibleSubmissions.find((submission) => submission.id === selectedSubmissionId) ??
    listRows[0]?.submission ??
    null;
  const selectedListRow = selectedSubmission
    ? listRows.find((row) =>
        row.submissions.some((submission) => submission.id === selectedSubmission.id)
      ) ?? null
    : null;
  const navigationSubmissions =
    viewMode === 'student' && selectedListRow
      ? selectedListRow.submissions
      : visibleSubmissions;
  const activeSubmissionId = selectedSubmission?.id ?? null;
  const selectedIndex = selectedSubmission
    ? navigationSubmissions.findIndex(
        (submission) => submission.id === selectedSubmission.id
      )
    : -1;
  const activeDetails =
    submissionDetails?.submission.id === activeSubmissionId
      ? submissionDetails
      : null;
  const selectedDimensionIds =
    activeDetails?.submission.dimension_ids &&
    activeDetails.submission.dimension_ids.length > 0
      ? activeDetails.submission.dimension_ids
      : activeDetails?.scores.map((score) => score.dimension_id) ?? [];
  const detailDimensions =
    selectedDimensionIds.length > 0
      ? selectedDimensionIds
          .map((dimensionId) =>
            dimensions.find((dimension) => dimension.id === dimensionId)
          )
          .filter((dimension): dimension is Dimension => Boolean(dimension))
      : dimensions.slice(0, 9);
  const detailScores = activeDetails?.scores ?? [];
  const detailAverage =
    detailScores.length > 0
      ? detailScores.reduce((sum, score) => sum + score.score, 0) / detailScores.length
      : selectedSubmission
        ? getAverageScore(selectedSubmission)
        : 0;
  const detailMaxScore = Math.max(detailDimensions.length * 3, 0);
  const detailTotal = Math.round(detailAverage * detailDimensions.length);
  const statusCounts: Record<SubmissionStatusFilter, number> = {
    all: courseFilteredSubmissions.length,
    handed: courseFilteredSubmissions.filter(
      (submission) => submission.status !== 'scoring'
    ).length,
    draft: courseFilteredSubmissions.filter(
      (submission) => submission.status === 'scoring'
    ).length,
  };
  const versionHistory = selectedSubmission
    ? submissions
        .filter(
          (submission) =>
            submission.project_id === selectedSubmission.project_id &&
            haveSameParticipants(submission, selectedSubmission)
        )
        .sort((first, second) => {
          const firstIteration = first.iteration_number ?? 0;
          const secondIteration = second.iteration_number ?? 0;

          if (firstIteration !== secondIteration) {
            return firstIteration - secondIteration;
          }

          return (
            new Date(first.submitted_at).getTime() -
            new Date(second.submitted_at).getTime()
          );
        })
    : [];
  const selectedCourseLabel =
    courseFilter === 'all'
      ? 'All Courses'
      : courses.find((course) => course.id === courseFilter)?.code || 'All Courses';
  const versionIndex = selectedSubmission
    ? versionHistory.findIndex((submission) => submission.id === selectedSubmission.id)
    : -1;
  const selectedVersionNumber =
    selectedSubmission?.iteration_number ?? (versionIndex >= 0 ? versionIndex + 1 : 1);
  const selectedVersionTotal = Math.max(versionHistory.length, selectedVersionNumber);
  const reviewSubtitle = selectedSubmission
    ? `${selectedSubmission.course_code} · ${selectedSubmission.course_title} · Submission ${selectedVersionNumber} of ${selectedVersionTotal}`
    : '';
  const aiSummary = activeDetails?.submission.ai_overall_summary || '';
  const aiStrengths = activeDetails?.submission.ai_overall_strengths || [];
  const aiImprovements = activeDetails?.submission.ai_priority_improvements || [];
  const hasAiFeedback =
    Boolean(aiSummary) || aiStrengths.length > 0 || aiImprovements.length > 0;
  const submissionEssay = activeDetails?.submission.essay_text?.trim() ?? '';
  const submissionFileList =
    activeDetails?.submission.file_urls && activeDetails.submission.file_urls.length > 0
      ? activeDetails.submission.file_urls
      : activeDetails?.submission.file_url
        ? [activeDetails.submission.file_url]
        : selectedSubmission?.file_url
          ? [selectedSubmission.file_url]
          : [];
  const submissionFileNameList = getSubmissionFileDisplayNames(
    submissionFileList,
    activeDetails?.submission.file_names,
  );
  const hasSubmissionContent = submissionEssay.length > 0 || submissionFileList.length > 0;
  const submissionWordCount = submissionEssay ? getWordCount(submissionEssay) : 0;
  const reviewBodyStyle: SubmissionReviewBodyStyle = isSubmissionDrawerOpen
    ? { '--submission-drawer-width': `${submissionDrawerWidth}px` }
    : {};

  useEffect(() => {
    let isMounted = true;

    const fetchAllSubmissions = async () => {
      try {
        setLoading(true);
        setError(null);
        const [coursesData, dimensionsData] = await Promise.all([
          instructorService.getCourses(),
          instructorService.getDimensions(),
        ]);
        const courseSubmissions = await Promise.all(
          coursesData.map(async (course) => {
            const rows = await instructorService.getCourseSubmissions(course.id);

            return rows.map((submission) => ({
              ...submission,
              course_id: course.id,
              course_code: course.code,
              course_title: course.title,
            }));
          })
        );
        const nextSubmissions = courseSubmissions
          .flat()
          .sort(
            (first, second) =>
              new Date(second.submitted_at).getTime() -
              new Date(first.submitted_at).getTime()
          );

        if (!isMounted) {
          return;
        }

        setCourses(coursesData);
        setDimensions(dimensionsData);
        setSubmissions(nextSubmissions);
        setSelectedSubmissionId(submissionId ?? nextSubmissions[0]?.id ?? null);

        if (submissionId) {
          setIsSubmissionDrawerOpen(true);
        }
      } catch (err) {
        console.error('Error fetching submissions:', err);

        if (isMounted) {
          setError('Failed to load submissions');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAllSubmissions();

    return () => {
      isMounted = false;
    };
  }, [submissionId]);

  useEffect(() => {
    if (!submissionId || submissions.length === 0) {
      return;
    }

    if (submissions.some((submission) => submission.id === submissionId)) {
      setSelectedSubmissionId(submissionId);
      setIsSubmissionDrawerOpen(true);
    }
  }, [submissionId, submissions]);

  useEffect(() => {
    setIsRubricModalOpen(false);
  }, [activeSubmissionId]);

  useEffect(() => {
    if (!selectedSubmission) {
      setSubmissionDetails(null);
      setSuggestionText('');
      return;
    }

    let isMounted = true;

    const fetchDetails = async () => {
      try {
        setDetailsLoading(true);
        const details = await instructorService.getSubmissionDetails(selectedSubmission.id);

        if (!isMounted) {
          return;
        }

        setSubmissionDetails(details);
        setSuggestionText(
          details.submission.instructor_suggestion?.suggestion_text || ''
        );
      } catch (err) {
        console.error('Error fetching submission details:', err);

        if (isMounted) {
          setSubmissionDetails(null);
          setSuggestionText('');
        }
      } finally {
        if (isMounted) {
          setDetailsLoading(false);
        }
      }
    };

    fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [selectedSubmission]);

  const handleSaveSuggestion = async () => {
    if (!selectedSubmission || !suggestionText.trim()) {
      return;
    }

    try {
      setSavingSuggestion(true);
      await instructorService.upsertSubmissionSuggestion(
        selectedSubmission.id,
        suggestionText.trim()
      );
      const details = await instructorService.getSubmissionDetails(selectedSubmission.id);
      setSubmissionDetails(details);
      setSuggestionText(
        details.submission.instructor_suggestion?.suggestion_text || suggestionText.trim()
      );

      const recipientName =
        selectedSubmission.student_name.trim().split(/\s+/)[0] || selectedSubmission.student_name;
      showToast(`Suggestion saved for ${recipientName}`);
    } catch (err) {
      console.error('Error saving suggestion:', err);
      showToast('Failed to save suggestion. Please try again.', 'error');
    } finally {
      setSavingSuggestion(false);
    }
  };

  const handleDownloadFile = async (fileIndex: number, fileName: string) => {
    if (!selectedSubmission) {
      return;
    }

    const fileKey = `${selectedSubmission.id}-${fileIndex}`;

    try {
      setDownloadingFileKey(fileKey);
      const fileBlob = await instructorService.downloadSubmissionFile(
        selectedSubmission.id,
        fileIndex,
      );
      const downloadUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    } catch (error) {
      console.error('Error downloading submission file:', error);
      showToast('Failed to download file. Please try again.', 'error');
    } finally {
      setDownloadingFileKey(null);
    }
  };

  const selectRelativeSubmission = (offset: number) => {
    if (selectedIndex < 0) {
      return;
    }

    const nextSubmission = navigationSubmissions[selectedIndex + offset];

    if (nextSubmission) {
      setSelectedSubmissionId(nextSubmission.id);
    }
  };

  const handleDrawerResizeStart = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizingDrawer(true);
  };

  const handleDrawerResizeMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isResizingDrawer) {
      return;
    }

    const reviewBody = event.currentTarget.closest('.instructor-review-body');

    if (!reviewBody) {
      return;
    }

    const bounds = reviewBody.getBoundingClientRect();
    const nextWidth = Math.round(bounds.right - event.clientX);

    setSubmissionDrawerWidth(Math.min(680, Math.max(320, nextWidth)));
  };

  const handleDrawerResizeEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setIsResizingDrawer(false);
  };

  const handleDrawerResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    setSubmissionDrawerWidth((current) => {
      const delta = event.key === 'ArrowLeft' ? 32 : -32;

      return Math.min(680, Math.max(320, current + delta));
    });
  };

  if (loading) {
    return (
      <div className="instructor-page instructor-design-page instructor-page-fixed">
        <InstructorLoadingSkeleton
          message="Loading submissions..."
          variant="submissions"
        />
      </div>
    );
  }

  return (
    <div className="instructor-page instructor-design-page instructor-page-fixed">
      <div className="instructor-top-bar">
        <div>
          <h1 className="instructor-page-title">Submissions</h1>
          <div className="instructor-page-sub">
            Review student work, scores, and AI-generated feedback
          </div>
        </div>
        <div className="instructor-course-selector">
          <button
            aria-expanded={isCourseMenuOpen}
            aria-haspopup="listbox"
            className={
              isCourseMenuOpen
                ? 'instructor-course-picker open'
                : 'instructor-course-picker'
            }
            onClick={() => setIsCourseMenuOpen((current) => !current)}
            type="button"
          >
            <BookOpen strokeWidth={2} />
            <span>{selectedCourseLabel}</span>
            <ChevronDown className="instructor-course-picker-chevron" strokeWidth={2.4} />
          </button>
          {isCourseMenuOpen && (
            <div className="instructor-course-menu" role="listbox">
              <button
                className={courseFilter === 'all' ? 'selected' : ''}
                onClick={() => {
                  setCourseFilter('all');
                  setIsCourseMenuOpen(false);
                }}
                type="button"
              >
                All Courses
              </button>
              {courses.map((course) => (
                <button
                  className={courseFilter === course.id ? 'selected' : ''}
                  key={course.id}
                  onClick={() => {
                    setCourseFilter(course.id);
                    setIsCourseMenuOpen(false);
                  }}
                  type="button"
                >
                  {course.code} - {course.title}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div className="instructor-error-state compact">{error}</div>}

      <div className="instructor-submissions-grid-v2">
        <aside className="instructor-submission-list-panel">
          <div className="instructor-list-header">
            <div className="instructor-list-title-row">
              <div className="instructor-list-title">
                <span>{viewMode === 'student' ? 'Students' : 'Submissions'}</span>
                <span className="instructor-list-count">{listRows.length}</span>
              </div>
            </div>
            <div className="instructor-search-wrap design-search">
              <Search strokeWidth={2} />
              <input
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by student or project..."
                type="search"
                value={searchTerm}
              />
            </div>
            <div className="instructor-view-toggle" role="tablist">
              <button
                className={viewMode === 'submission' ? 'active' : ''}
                onClick={() => setViewMode('submission')}
                type="button"
              >
                <FileText strokeWidth={2} />
                By submission
              </button>
              <button
                className={viewMode === 'student' ? 'active' : ''}
                onClick={() => setViewMode('student')}
                type="button"
              >
                <UserRound strokeWidth={2} />
                By student
              </button>
            </div>
            <div className="instructor-filter-pills">
              {statusFilters.map((filter) => (
                <button
                  className={filterStatus === filter.key ? 'active' : ''}
                  key={filter.key}
                  onClick={() => setFilterStatus(filter.key)}
                  type="button"
                >
                  {filter.label}{' '}
                  <span>{statusCounts[filter.key]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="instructor-submission-list-scroll">
            {listRows.length === 0 ? (
              <div className="instructor-empty-state">No submissions found.</div>
            ) : (
              listRows.map((row) => {
                const submission = row.submission;
                const isActive =
                  viewMode === 'student'
                    ? row.submissions.some(
                        (candidate) => candidate.id === selectedSubmission?.id
                      )
                    : submission.id === selectedSubmission?.id;

                return (
                  <button
                    className={isActive ? 'instructor-sub-card active' : 'instructor-sub-card'}
                    key={row.key}
                    onClick={() => setSelectedSubmissionId(submission.id)}
                    type="button"
                  >
                    <div className="instructor-sub-card-header">
                      <span className="instructor-sub-card-title">{row.displayName}</span>
                      <span className={`instructor-status-badge ${getStatusClass(submission.status)}`}>
                        <span className="instructor-status-dot" />
                        {getStatusLabel(submission.status)}
                      </span>
                    </div>
                    <div className="instructor-sub-card-project">
                      {viewMode === 'student'
                        ? `Latest: ${submission.project_title}`
                        : submission.project_title}
                    </div>
                    {row.secondary && (
                      <div className="instructor-sub-card-via">
                        {viewMode === 'student' ? (
                          <FileText strokeWidth={2} />
                        ) : (
                          <Users strokeWidth={2} />
                        )}
                        {row.secondary}
                      </div>
                    )}
                    <div className="instructor-sub-card-score-row">
                      <span className="instructor-score-bar">
                        <span
                          className="instructor-score-fill"
                          style={{ width: `${getScorePercent(submission)}%` }}
                        />
                      </span>
                      <strong>{getScoreLabel(submission)}</strong>
                    </div>
                    <div className="instructor-sub-card-date">
                      {formatDate(submission.submitted_at)}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <section className="instructor-review-panel">
          {selectedSubmission ? (
            <>
              <div className="instructor-review-header">
                <div className="instructor-review-title-wrap">
                  <div className="instructor-review-kicker">
                    <span className="dot" />
                    {getStatusLabel(selectedSubmission.status)}
                    <span className="type-pill">{selectedSubmission.project_type}</span>
                  </div>
                  <h2 className="instructor-review-title">
                    {selectedSubmission.student_name} · {selectedSubmission.project_title}
                  </h2>
                  <div className="instructor-review-subtitle">
                    {reviewSubtitle}
                  </div>
                </div>
                <div className="instructor-review-actions">
                  <button
                    className={
                      isSubmissionDrawerOpen
                        ? 'instructor-secondary-button instructor-submission-toggle active'
                        : 'instructor-secondary-button instructor-submission-toggle'
                    }
                    onClick={() => setIsSubmissionDrawerOpen((current) => !current)}
                    type="button"
                  >
                    <ChevronsRight className="h-4 w-4" strokeWidth={2.4} />
                    {isSubmissionDrawerOpen ? 'Hide submission' : 'Show submission'}
                  </button>
                  <button
                    aria-label="Previous submission"
                    className="instructor-icon-button light"
                    disabled={selectedIndex <= 0}
                    onClick={() => selectRelativeSubmission(-1)}
                    type="button"
                  >
                    <ChevronLeft strokeWidth={2.4} />
                  </button>
                  <span className="instructor-review-position">
                    {selectedIndex + 1} of {navigationSubmissions.length}
                  </span>
                  <button
                    aria-label="Next submission"
                    className="instructor-icon-button light"
                    disabled={selectedIndex >= navigationSubmissions.length - 1}
                    onClick={() => selectRelativeSubmission(1)}
                    type="button"
                  >
                    <ChevronRight strokeWidth={2.4} />
                  </button>
                </div>
              </div>

              {versionHistory.length > 0 && (
                <div className="instructor-version-row">
                  <span>Version History</span>
                  {versionHistory.map((submission, index) => {
                    const isLatest = index === versionHistory.length - 1;
                    const versionLabel = submission.iteration_number ?? index + 1;

                    return (
                      <button
                        className={submission.id === selectedSubmission.id ? 'active' : ''}
                        key={submission.id}
                        onClick={() => setSelectedSubmissionId(submission.id)}
                        type="button"
                      >
                        V{versionLabel}
                        {isLatest ? (
                          <span className="instructor-version-latest">Latest</span>
                        ) : (
                          <Lock className="instructor-version-lock" strokeWidth={2.3} />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <div
                className={
                  isSubmissionDrawerOpen
                    ? 'instructor-review-body drawer-open'
                    : 'instructor-review-body'
                }
                style={reviewBodyStyle}
              >
                <aside className="instructor-review-context">
                  <section className="instructor-info-section">
                    <h3><UserRound strokeWidth={2} />Student</h3>
                    <div className="instructor-info-row">
                      <span>Name</span>
                      <strong>{selectedSubmission.student_name}</strong>
                    </div>
                    <div className="instructor-info-row">
                      <span>Student ID</span>
                      <strong>{selectedSubmission.student_id}</strong>
                    </div>
                    <div className="instructor-info-row">
                      <span>Email</span>
                      <strong>{selectedSubmission.student_email}</strong>
                    </div>
                  </section>

                  <section className="instructor-info-section">
                    <h3><FileText strokeWidth={2} />Submission</h3>
                    <div className="instructor-info-row">
                      <span>Project</span>
                      <strong>{selectedSubmission.project_title}</strong>
                    </div>
                    <div className="instructor-info-row">
                      <span>Submitted</span>
                      <strong>{formatDateTime(selectedSubmission.submitted_at)}</strong>
                    </div>
                    <div className="instructor-info-row">
                      <span>Status</span>
                      <strong>{getStatusLabel(selectedSubmission.status)}</strong>
                    </div>
                  </section>

                  <section className="instructor-score-summary-card">
                    <h3>Score Summary</h3>
                    <div className="instructor-score-big">
                      {detailAverage.toFixed(1)}<span>/3</span>
                    </div>
                    <p>
                      Total {detailTotal}/{detailMaxScore} across {detailDimensions.length} dimensions
                    </p>
                    {detailsLoading ? (
                      <div className="instructor-mini-radar-empty">Loading score profile...</div>
                    ) : (
                      <MiniRadar dimensions={detailDimensions} scores={detailScores} />
                    )}
                    <div className="instructor-score-summary-footer">
                      <span><strong>{detailScores.length}</strong> / {detailDimensions.length} dimensions scored</span>
                      <button
                        disabled={detailsLoading || detailDimensions.length === 0}
                        onClick={() => setIsRubricModalOpen(true)}
                        type="button"
                      >
                        View Rubric
                      </button>
                    </div>
                  </section>
                </aside>

                <div className="instructor-review-main">
                  <section className="instructor-ai-card">
                    <h3><Sparkles strokeWidth={2.2} />AI Feedback Summary</h3>
                    {detailsLoading ? (
                      <p>Loading AI feedback...</p>
                    ) : hasAiFeedback ? (
                      <>
                        {aiSummary && (
                          <p>{aiSummary}</p>
                        )}
                        {aiStrengths.length > 0 && (
                            <>
                              <div className="instructor-ai-divider" />
                              <h4 className="strengths"><CheckCircle strokeWidth={2.2} />Strengths</h4>
                              <ul>
                                {aiStrengths.map((strength) => (
                                  <li key={strength}><CheckCircle strokeWidth={2.2} />{strength}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        {aiImprovements.length > 0 && (
                            <>
                              <div className="instructor-ai-divider" />
                              <h4 className="improve"><AlertTriangle strokeWidth={2.2} />Areas for Improvement</h4>
                              <ul>
                                {aiImprovements.map((improvement) => (
                                  <li key={improvement}><AlertTriangle strokeWidth={2.2} />{improvement}</li>
                                ))}
                              </ul>
                            </>
                          )}
                      </>
                    ) : (
                      <p>No AI feedback has been generated for this submission yet.</p>
                    )}
                  </section>

                  <section className="instructor-suggestion-card">
                    <h3><SquarePen strokeWidth={2.2} />Instructor Suggestions</h3>
                    <p>
                      Optional. Your suggestions will be visible to the student alongside the AI feedback.
                    </p>
                    <textarea
                      maxLength={5000}
                      onChange={(event) => setSuggestionText(event.target.value)}
                      placeholder={`Write a personal note to ${selectedSubmission.student_name.split(' ')[0]} - for example, point to a specific paragraph that worked well, or suggest a concrete revision step...`}
                      value={suggestionText}
                    />
                    <div className="instructor-suggestion-footer">
                      <span>{suggestionText.length} / 5000 characters</span>
                      <button
                        disabled={savingSuggestion || !suggestionText.trim()}
                        onClick={handleSaveSuggestion}
                        type="button"
                      >
                        {savingSuggestion ? 'Saving...' : 'Save Suggestion'}
                      </button>
                    </div>
                  </section>
                </div>

                {isSubmissionDrawerOpen && (
                  <>
                    <div
                      aria-label="Resize submission panel"
                      aria-orientation="vertical"
                      aria-valuemax={680}
                      aria-valuemin={320}
                      aria-valuenow={submissionDrawerWidth}
                      className={
                        isResizingDrawer
                          ? 'instructor-drawer-resize-handle resizing'
                          : 'instructor-drawer-resize-handle'
                      }
                      onKeyDown={handleDrawerResizeKeyDown}
                      onPointerCancel={handleDrawerResizeEnd}
                      onPointerDown={handleDrawerResizeStart}
                      onPointerMove={handleDrawerResizeMove}
                      onPointerUp={handleDrawerResizeEnd}
                      role="separator"
                      tabIndex={0}
                    />
                    <aside className="instructor-submission-drawer">
                      <div className="instructor-submission-drawer-header">
                        <div>
                          <h3><FileText strokeWidth={2.2} />Student Submission</h3>
                          <p>
                            <strong>{selectedSubmission.project_title}</strong> · v{selectedVersionNumber} ·{' '}
                            {formatDateTime(selectedSubmission.submitted_at)}
                          </p>
                        </div>
                        <button
                          aria-label="Close submission drawer"
                          className="instructor-icon-button light"
                          onClick={() => setIsSubmissionDrawerOpen(false)}
                          type="button"
                        >
                          <X strokeWidth={2.4} />
                        </button>
                      </div>
                      <div className="instructor-submission-drawer-body">
                        {detailsLoading ? (
                          <div className="instructor-empty-state compact">
                            Loading submission content...
                          </div>
                        ) : !hasSubmissionContent ? (
                          <div className="instructor-empty-state compact">
                            No submission content available.
                          </div>
                        ) : (
                          <>
                            {submissionEssay && (
                              <section className="instructor-submission-text-card">
                                <div className="instructor-submission-meta-bar">
                                  <span>{selectedSubmission.project_title} · v{selectedVersionNumber}</span>
                                  <strong>
                                    {submissionWordCount} words · {formatDateTime(selectedSubmission.submitted_at)}
                                  </strong>
                                </div>
                                <div className="instructor-submission-text">
                                  {submissionEssay}
                                </div>
                              </section>
                            )}

                            {submissionFileList.length > 0 && (
                              <section className="instructor-submission-files-card">
                                <h4>{submissionFileList.length === 1 ? 'File' : 'Files'}</h4>
                                <div className="instructor-submission-file-list">
                                  {submissionFileList.map((url, index) => (
                                    <button
                                      className="instructor-secondary-button justify-start"
                                      disabled={
                                        downloadingFileKey ===
                                        `${selectedSubmission.id}-${index}`
                                      }
                                      key={`${url}-${index}`}
                                      onClick={() =>
                                        handleDownloadFile(
                                          index,
                                          submissionFileNameList[index],
                                        )
                                      }
                                      type="button"
                                    >
                                      <Download className="h-4 w-4" strokeWidth={2.4} />
                                      <span>
                                        {downloadingFileKey ===
                                        `${selectedSubmission.id}-${index}`
                                          ? 'Downloading...'
                                          : submissionFileNameList[index]}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </section>
                            )}
                          </>
                        )}
                      </div>
                    </aside>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="instructor-empty-state">Select a submission to review.</div>
          )}
        </section>
      </div>

      {isRubricModalOpen && selectedSubmission && (
        <RubricModal
          dimensions={detailDimensions}
          onClose={() => setIsRubricModalOpen(false)}
          scores={detailScores}
          submission={selectedSubmission}
          submissionDetails={activeDetails}
        />
      )}

      <InstructorToast toast={toast} />
    </div>
  );
};

export { InstructorSubmissions };
